/**
 * PatientHomePage — the patient's home hub (/dashboard).
 *
 * A calm, centered landing in the style of the AI apps: a greeting, the two things a
 * patient comes to ask (CareStart, Medical Library) as the larger choices, and their
 * records as a compact set below. Everything is the shared SelectableChoiceCard so it
 * inherits the theme's outlined-surface + hover/focus treatment.
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Grid, Typography } from "@mui/material";
import RecordVoiceOverRounded from "@mui/icons-material/RecordVoiceOverRounded";
import SchoolRounded from "@mui/icons-material/SchoolRounded";
import HealthAndSafetyRounded from "@mui/icons-material/HealthAndSafetyRounded";
import MedicationRounded from "@mui/icons-material/MedicationRounded";
import AssignmentRounded from "@mui/icons-material/AssignmentRounded";
import QrCode2Rounded from "@mui/icons-material/QrCode2Rounded";

import { useAuth } from "hooks/useAuth";
import SelectableChoiceCard from "components/common/SelectableChoiceCard";
import { masterChartPath } from "constants/masterChartRoutes";
import { BrandGlowLayer } from "components/common/BrandGlow";

// Titles and descriptions are "home" namespace keys, translated at render; product
// names (CareStart, MasterChart) stay as they are in every language.
// Hidden for now, each for its own reason:
// - VisitStart: not finished. Patients still reach it from a clinic's QR code.
// - Messages: facility <> patient comms aren't live; its route is commented out
//   in PatientRoutes.jsx, so the tile led nowhere.
const ASK_TILES = [
  {
    icon: <RecordVoiceOverRounded />,
    title: "CareStart",
    descriptionKey: "patientHome.tiles.careStart",
    path: "/dashboard/carestart",
  },
  {
    icon: <SchoolRounded />,
    titleKey: "patientHome.tiles.medicalLibraryTitle",
    descriptionKey: "patientHome.tiles.medicalLibrary",
    path: "/dashboard/medical-library",
  },
];

const RECORD_TILES = [
  {
    icon: <HealthAndSafetyRounded />,
    title: "MasterChart",
    descriptionKey: "patientHome.tiles.masterChart",
    path: masterChartPath(),
  },
  {
    icon: <MedicationRounded />,
    titleKey: "patientHome.tiles.medicationsTitle",
    descriptionKey: "patientHome.tiles.medications",
    path: "/dashboard/medications",
  },
  {
    icon: <AssignmentRounded />,
    titleKey: "patientHome.tiles.dischargeTitle",
    descriptionKey: "patientHome.tiles.discharge",
    path: "/dashboard/discharge-instructions",
  },
  {
    icon: <QrCode2Rounded />,
    titleKey: "patientHome.tiles.shareTitle",
    descriptionKey: "patientHome.tiles.share",
    path: masterChartPath("share"),
  },
];

const PatientHomePage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("home");
  const { user } = useAuth();
  // Sign-up never asks for a name, so the greeting can't use one. First visit is the
  // sign-in that created the account.
  const firstVisit =
    user?.metadata?.creationTime &&
    user.metadata.creationTime === user.metadata.lastSignInTime;

  return (
    <Box
      sx={{
        // Not positioned, so BrandGlowLayer fills the whole <main>, not this column;
        // isolation keeps it behind the cards but above the page.
        isolation: "isolate",
        maxWidth: 760,
        mx: "auto",
        // Centered in the viewport like the AI apps' start screens; the offsets are
        // the dashboard page column's padding and, on phones, the top app bar.
        minHeight: "calc(100dvh - 128px)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        py: 3,
      }}
    >
      <BrandGlowLayer preset="home" />
      {/* Same size as the other page titles (DashboardPageHeader). */}
      <Typography
        variant="h4"
        component="h1"
        textAlign="center"
        sx={{ fontSize: { xs: "1.5rem", sm: "2.125rem" } }}
      >
        {firstVisit ? t("greeting.welcome") : t("greeting.welcomeBack")}
      </Typography>
      <Typography
        color="text.secondary"
        textAlign="center"
        sx={{ mt: 1, mb: { xs: 3, md: 4 } }}
      >
        {t("greeting.prompt")}
      </Typography>

      <Grid container spacing={2}>
        {ASK_TILES.map((tile) => (
          <Grid item xs={12} sm={6} key={tile.path}>
            <SelectableChoiceCard
              icon={tile.icon}
              title={tile.titleKey ? t(tile.titleKey) : tile.title}
              description={t(tile.descriptionKey)}
              onClick={() => navigate(tile.path)}
              alignIcon="center"
              sx={{ py: 2.5 }}
            />
          </Grid>
        ))}
      </Grid>

      <Typography
        variant="subtitle2"
        color="text.secondary"
        sx={{ mt: { xs: 3, md: 4 }, mb: 1 }}
      >
        {t("patientHome.yourRecords")}
      </Typography>
      <Grid container spacing={1.5}>
        {RECORD_TILES.map((tile) => (
          <Grid item xs={12} sm={6} key={tile.path}>
            <SelectableChoiceCard
              icon={tile.icon}
              title={tile.titleKey ? t(tile.titleKey) : tile.title}
              description={t(tile.descriptionKey)}
              onClick={() => navigate(tile.path)}
              alignIcon="center"
              sx={{ py: 1.5 }}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default PatientHomePage;
